import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FileInputComponent} from "ngx-material-file-input";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../../shared/services/form-helper.service";
import {CommissionImportService} from "../../../../shared/services/commission-import.service";
import {DOCUMENT} from "@angular/common";
import {Router} from "@angular/router";
import {DictService} from "../../../../shared/services/dict.service";
import {ReceivablesImportsService} from "../../../../shared/services/receivables-imports.service";
import {ReplaySubject} from "rxjs";
import {InsuranceCompany} from "../../../../shared/interface/insurance-company";
import {delay, first, takeUntil} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {FileBase64} from "../../../../shared/components/file-drag-and-drop/file-drag-and-drop.component";
import {Dictionary} from "../../../../shared/interface/dictionary";
import {AuthService} from "../../../../shared/services/auth.service";
import {UserPermissions} from "../../../../shared/interface/user-permissions";

@Component({
  selector: 'app-receivables-import',
  templateUrl: './receivables-import.component.html',
  styleUrls: ['./receivables-import.component.scss']
})
export class ReceivablesImportComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInputComponent: FileInputComponent;

  importCompanyId: ReplaySubject<number> = new ReplaySubject<number>(1);
  receivablesImportFormValid = false;
  receivablesImportForm: FormGroup;
  companyControl: FormControl;
  insuranceCompanyControl: FormControl;
  importerControl: FormControl;
  overCommissionControl: FormControl;
  profitControl: FormControl;
  cancellationControl: FormControl;
  fileControl: FormControl;
  fileMaxSize = 5242880 * 20;
  processingRequest = false;
  formErrors = {};
  selectedInsuranceCompany: InsuranceCompany;
  insuranceRequired = true;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private receivablesImportService: ReceivablesImportsService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private dictService: DictService,
    private auth: AuthService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.receivablesImportForm = fb.group({
      name: fb.control('', [Validators.required]),
      company: this.companyControl = fb.control('', [Validators.required]),
      overCommission: this.overCommissionControl = fb.control(''),
      profitImport: this.profitControl = fb.control(''),
      cancellation: this.cancellationControl = fb.control('' ),
      insuranceCompany: this.insuranceCompanyControl = fb.control('', [Validators.required]),
      fileContent: fb.control('', [Validators.required]),
      importer: this.importerControl = fb.control('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.receivablesImportForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.receivablesImportFormValid = this.receivablesImportForm.valid;
      });

    this.companyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((companyId: number) => this.importCompanyId.next(companyId));

    this.insuranceCompanyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((icId: number) => {
        if (icId === 18 || icId === 53) {
          this.companyControl.reset();
          this.companyControl.disable();
        }else {
          this.companyControl.enable();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  import(): void {
    if (!this.receivablesImportForm.valid) {
      this.receivablesImportForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    if (this.processingRequest ){
      return;
    }
    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.receivablesImportForm, this.formErrors);
    this.formErrors = {};
    const formData = {...this.receivablesImportForm.value};
    formData.company = this.companyControl.value;

    this.receivablesImportService.import(formData)
      .pipe(
        first()
      )
      .subscribe(() => {
        this.snack.open('Rozpoczynanie importowania danych', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.router.navigate(['/naleznosci/raporty']);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snack.open('Podczas tworzenia importu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.receivablesImportForm, this.formErrors);
        }
      })
    ;
  }

  fileSelected(file: FileBase64): void {
    this.receivablesImportForm.get('fileContent').setValue(file?.base64);
    this.receivablesImportForm.get('name').setValue(file?.filename);
  }

  onAvailableImportersChange(items: Dictionary[]): void {
    if (items.length > 0) {
      this.importerControl.enable();
    } else {
      this.importerControl.disable();
    }
  }

}
